import type { CommonTranslations } from "../en/Common";
import type { TranslationShape } from "../../store/types";

export const commonEs: TranslationShape<CommonTranslations> = {
  na: "N/A",
  itemSwappedOut: "Artículo intercambiado fuera:",
  itemSwappedIn: "Artículo intercambiado en:",
  facingsChange: "Frentes:",
  swapFacingsModeLabel: "Modo de facings",
  swapFacingsModeMatch: "Coincidir con facings del producto saliente",
  swapFacingsModeFree: "Dejar que DOS balancer decida",
  multiplePlacementFailures: "Múltiples errores de colocación",
  updateReport: {
    facingsExpanded: "FRENTES EXPANDIDOS",
    facingsReduced: "FRENTES REDUCIDOS",
    item: "ARTÍCULO",
    items: "ARTÍCULOS",
  },
  search: "Buscar",
  close: "Cerrar",
  cancel: "Cancelar",
  save: "Guardar",
  delete: "Eliminar",
  edit: "Editar",
  view: "Ver",
  back: "Volver",
  next: "Siguiente",
  loading: "Cargando...",
  noResults: "No se encontraron resultados",
  storePogExport: {
    forceOverwritePdfs: "Forzar sobrescritura de PDF",
    latestVersionNote: "Exporta el planograma más reciente de cada tienda, no la versión mostrada.",
    approvedVersionNote:
      "Exporta el planograma aprobado de cada tienda, sea cual sea la versión aprobada.",
    csvColumns: {
      title: "Elegir columnas CSV",
      rangedItems: "Artículos en surtido",
      unrangedItems: "Artículos fuera del surtido",
      loading: "Cargando columnas...",
      loadFailed: "No se pudieron cargar las columnas CSV. Inténtalo de nuevo.",
      loadFailedToast: "No se pudieron cargar las columnas de exportación CSV",
      retry: "Reintentar",
      search: "Buscar columnas",
      selectedCount: "{{selected}} de {{total}} columnas seleccionadas",
      noMatches: "Ninguna columna coincide con tu búsqueda.",
      export: "Exportar CSV",
    },
  },
  download: "Descargar",
  generate: "Generar",
  status: "Estado",
  category: "Categoría",
  store: "Tienda",
  stores: "Tiendas",
  cluster: "Cluster",
  brand: "Marca",
  critical: "Crítico",
  warning: "Advertencia",
  medium: "Medio",
  low: "Bajo",
  active: "Activo",
  shelves: "Estantes",
  start: "Inicio: {{date}}",
  end: "Fin: {{date}}",
  alerts: "Alertas ({{count}})",
  before: "Antes",
  after: "Después",
  change: "Cambio",
  current: "Actual",
  salesValue: "Valor de Ventas",
  unitsSold: "Unidades Vendidas",
  grossMargin: "Margen Bruto",
  inventoryTurnover: "Rotación de Inventario",
  daysOfInventory: "Días de Inventario",
  outOfStock: "Agotado (%)",
  pareto: "Pareto 80/20",
  premium: "Premium",
  urban: "Urbano",
  suburban: "Suburbano",
  language: "Idioma",
  shelf: "Estante",
  selectedShelves: "Estantes seleccionados",
  selectedShelf: "Estante seleccionado",
  shelvesCount: "{{count}} estantes",
  shelfName: "Estante: {{name}}",
  shelfNotchPrefix: " N:{{notch}}",
  shelfEditor: {
    label: "Editor de estante",
  },
  shelfTypes: {
    label: "Tipo de estante",
    regular: "Regular",
    riser: "Elevador",
    hangCell: "Celda colgante",
    setFailed: "No se pudo establecer el tipo de estante",
  },
  // Vocabulario de peso: el máximo de una repisa es su LÍMITE, nunca su
  // "capacidad" — capacidad ya significa unidades en esta pantalla.
  planogramRules: {
    weightRules: {
      tab: "Peso",
      shelfCap: {
        title: "Límite de carga del estante",
        limit: "Carga máxima por estante (gramos)",
        pounds: "{{pounds}} lb",
        noCap: "Sin límite — los estantes se reportan pero no se evalúan",
      },
      heavyRule: {
        title: "Los productos pesados van abajo",
        threshold: "Pesado a partir de (gramos)",
        thresholdPounds: "{{pounds}} lb",
        maxDeckY: "Línea de altura",
        maxDeckYHelp:
          "Una coordenada en la misma escala que la geometría de tus muebles, leída del planograma. No son milímetros ni pulgadas — son los mismos números que ya usan tus bahías y estantes.",
        maxDeckYRequired:
          "Obligatorio — no hay un valor predeterminado seguro. Léelo de tus propios muebles; esta regla permanece desactivada hasta que ingreses uno.",
      },
      passes: {
        resolveTitle: "Estantes sobrecargados",
        resolve: "Resolver estantes sobrecargados",
        resolveHelp:
          "Reduce la profundidad en estantes que superan el límite, nunca por debajo del mínimo de suministro; luego elimina el producto con peor valor por peso hasta que el estante cumpla su límite.",
        swapTitle: "Ubicación de marcas pesadas",
        swap: "Bajar marcas pesadas",
        swapHelp:
          "Intercambia bloques completos de marca entre un estante alto y uno bajo para que el producto pesado quede bajo la línea de altura, manteniendo cada marca contigua.",
        swapNeedsHeavyRule:
          'Activa "Los productos pesados van abajo" para darle sus números a esta regla.',
      },
      advanced: {
        title: "Avanzado",
        blockLevel: "Nivel de bloque de marca",
        blockLevelHelp:
          "Qué nivel de CDT identifica una marca para el intercambio. Lavandería lleva la marca en cdt2; otra categoría puede diferir.",
        fillFloor: "Llenado mínimo conservado tras un intercambio",
      },
    },
  },
  shelfWeight: {
    label: "Peso",
    load: "{{pounds}} lb",
    // Procedencia: un peso declarado y uno estimado no deben leerse igual.
    loadWithNotes: "{{pounds}} lb ({{notes}})",
    measured: "medido",
    allDerived: "estimado",
    someDerived: "{{count}} estimados",
    unweighed: "{{count}} sin pesar",
    bulk: "VOLUMINOSO",
    overloadedLabel: "Sobrecargado",
    overloadedRequestDefault: "supera el límite de {{pounds}} lb fijado para esta ejecución",
    overloadedFixture: "supera el límite del mueble de {{pounds}} lb",
    limitLabel: "Límite de peso",
    noLimit: "sin límite de peso fijado para esta ejecución",
    pogTotal: "Peso total",
  },
  widthFormatted: "{{value}} m {{cm}} cm",
  preparedBy: "Preparado Por",
  approvedBy: "Aprobado Por",
  categoryOwner: "Propietario de Categoría",
  dateEffective: "Fecha de Vigencia",
  segment: "Segmento",
  page: "Página",
  newItemSummary: "Resumen de Artículos Nuevos",
  deletedItemSummary: "Resumen de Artículos Eliminados",
  shelfSemanticReport: "Reporte Semántico de Estantería",
  width: "Ancho",
  depth: "Profundidad",
  height: "Alto",
  highlights: {
    defaultInsertedItemName: "Nuevo artículo insertado",
    newItemBadge: "PI",
    itemWeight: "Peso del artículo",
    itemWeightSublabel: "lb en el estante",
    shelfWeight: "Peso del estante",
    shelfWeightSublabel: "lb en la tabla",
    shelfWeightPct: "% del peso del estante",
    shelfWeightPctSublabel: "del límite",
    overloaded: "Sobrecargado",
    overloadedSublabel: "sobre el límite",
  },
  planogramCanvas: {
    rendererToggle: "Renderizador de lienzo",
    itemCount: "{{count}} artículos",
    newItemIndicator: "Indicador de artículo nuevo",
    itemMenu: {
      delete: "Eliminar",
      duplicate: "Duplicar",
      flipHorizontally: "Voltear horizontalmente",
      sideView: "Vista lateral",
      unrange: "Quitar del surtido",
    },
    bayMenu: {
      addBayLeft: "Agregar bahía a la izquierda",
      addBayRight: "Agregar bahía a la derecha",
      deleteBay: "Eliminar bahía",
      newShelf: "Estante nuevo",
      title: "Bahía {{bayNo}}",
    },
    containerMenu: {
      delete: "Eliminar",
      splitBottom: "Dividir hacia abajo",
      splitRight: "Dividir a la derecha",
      unrange: "Quitar del surtido",
    },
    shelfMenu: {
      delete: "Eliminar",
      duplicate: "Duplicar",
      flipHorizontally: "Voltear horizontalmente",
      mergeLeft: "Combinar a la izquierda",
      mergeRight: "Combinar a la derecha",
      moveDown: "Mover hacia abajo",
      moveUp: "Mover hacia arriba",
      sortCdt1: "Ordenar por prioridad CDT1",
      sortCdt2: "Ordenar por prioridad CDT2",
      sortCdt3: "Ordenar por prioridad CDT3",
      unmergeLeft: "Separar a la izquierda",
      unmergeRight: "Separar a la derecha",
      viewUnranged: "Ver productos fuera del surtido",
    },
  },
  planogramMetrics: {
    returnOnSpace: {
      title: "Retorno del espacio",
      description:
        "Indica si cada parte de la categoría recibe espacio de estante acorde con sus ventas. Una puntuación alta significa que ningún grupo tiene espacio insuficiente; asignar a un grupo más espacio del que genera no reduce la puntuación.",
    },
    coreRange: {
      title: "Surtido principal",
      description:
        "Indica cuántos productos imprescindibles llegaron realmente al estante. Los frentes adicionales del mismo producto cuentan una sola vez.",
      emptyReason:
        "Ningún producto de este planograma está marcado como surtido principal, por lo que no hay nada que medir.",
    },
    shelfAlignment: {
      title: "Alineación de estantes",
      description:
        "Indica si los estantes quedan a la misma altura a lo largo del mueble, de bahía a bahía. Los estantes cercanos pero desnivelados cuentan como error.",
    },
    dosMos: {
      title: "DOS / MOS",
      description:
        "Indica cuántos productos mantienen suficiente inventario en el estante para seguir vendiéndose entre entregas. Un producto reduce la puntuación si no alcanza su objetivo de días de suministro o de unidades en estante.",
    },
    aesthetics: {
      title: "Estética",
      description:
        "Indica lo ordenado y comprable que parece el planograma: bloques limpios, el orden propio de la categoría, estantes llenos y los paquetes pesados abajo. No dice nada sobre las ventas, así que léalo junto con las cifras que sí lo hacen.",
    },
    expectedSales: {
      title: "Ventas previstas",
      description:
        "Lo que se espera que venda todo lo incluido en este planograma, sumado; se cuenta una vez por producto, sean cuales sean sus frentes. Léalo junto a las puntuaciones de la izquierda: un planograma más ordenado que elimina productos se ve mejor allí y peor aquí.",
      emptyReason:
        "Este planograma no tiene cifras de ventas reales; cada producto lleva el mismo valor de marcador de posición, por lo que un total sería un número inventado. Vuelva a ejecutar la carga de datos para incorporar las ventas.",
    },
  },
  planogramProductDetails: {
    bulkWeight: "{{weight}} lb · VOLUMINOSO",
    weight: "Peso",
    weightValue: "{{weight}} lb",
  },
  pegboardFacingChange: {
    indicator: "Actualización de facing: {{changeType}}",
    inserted: "Insertado",
    swappedIn: "Intercambiado en",
    facingsIncreased: "Facings aumentados",
    facingsDecreased: "Facings reducidos",
    swappedOut: "Intercambiado fuera",
    deleted: "Eliminado",
  },
  editShelfModal: {
    menuLabel: "Editar estante",
    multipleShelvesEditUnavailable:
      "Editar estante solo está disponible cuando se selecciona un estante.",
    title: "Editar estante",
    widthLabel: "Ancho del estante (pulgadas)",
    widthPlaceholder: "Ingresa el ancho del estante",
    mergedWidthDisabled: "No se puede cambiar el ancho de los estantes combinados.",
    merchandisableWidthLabel: "Ancho comercializable (pulgadas)",
    merchandisableWidthPlaceholder: "Ingresa el ancho comercializable",
    mergedMerchandisableWidthDisabled:
      "No se puede cambiar el ancho comercializable de los estantes combinados.",
    merchandisableSpaceLabel: "Espacio comercializable",
    invalidWidth: "Ingresa un número positivo válido para el ancho del estante.",
    invalidMerchandisableWidth:
      "Ingresa un ancho comercializable válido dentro del ancho del estante.",
    alignLabel: "Alineación del estante",
    alignLeft: "Izquierda",
    alignRight: "Derecha",
    alignBetween: "Entre",
    alignAround: "Alrededor",
    changeShelfTypeTitle: "¿Cambiar el tipo de estante a {{shelfType}}?",
    incompatibleProductDescriptionOneShelf:
      "1 producto de este estante no se puede colocar en un accesorio {{shelfType}} ({{names}}). Al guardar, se moverá a la lista sin surtido.",
    incompatibleProductDescriptionMultipleShelves:
      "1 producto de estos estantes no se puede colocar en un accesorio {{shelfType}} ({{names}}). Al guardar, se moverá a la lista sin surtido.",
    incompatibleProductsDescriptionOneShelf:
      "{{count}} productos de este estante no se pueden colocar en un accesorio {{shelfType}} ({{names}}). Al guardar, se moverán a la lista sin surtido.",
    incompatibleProductsDescriptionMultipleShelves:
      "{{count}} productos de estos estantes no se pueden colocar en un accesorio {{shelfType}} ({{names}}). Al guardar, se moverán a la lista sin surtido.",
    incompatibleProductsAndMore: "y {{count}} más",
    changeAndUnrange: "Cambiar y quitar del surtido",
  },
  cdtTreeEditor: {
    button: "Árbol CDT",
    loadingCategory: "Cargando árbol CDT de categoría...",
    categoryLoadFailed: "No se pudo cargar el árbol CDT de categoría",
    invalidCategory: "El ID de categoría no es válido.",
    saveSuccess: "¡Jerarquía CDT guardada correctamente!",
    saveFailed: "No se pudo guardar la jerarquía CDT",
    saveUnavailable: "Este borrador CDT local no se puede guardar en la API.",
    saveNewUnavailable: "Guardar una nueva jerarquía CDT aún no está disponible.",
    nextUnavailable: "La navegación siguiente aún no está disponible.",
    resetSuccess: "Jerarquía CDT restablecida con datos ETL.",
    resetFailed: "No se pudo restablecer la jerarquía CDT.",
    resetRefreshFailed:
      "El restablecimiento se completó, pero no se pudo cargar la jerarquía actualizada. Recarga la página para continuar.",
    unassigned: "(Sin asignar)",
  },
  cdtWarnings: {
    nodeNameBlank: "El nombre del nodo no puede estar vacío.",
    subgroupNameBlank: "El nombre del subgrupo no puede estar vacío.",
    cannotAddBelow: "No se puede agregar un subgrupo debajo de CDT3.",
    saveParentFirst: "Guarda el nuevo padre antes de agregar otro subgrupo.",
    cdt0CannotReparent: "Los nodos CDT0 no se pueden reasignar.",
    cannotReparentIntoSubtree: "Un nodo no se puede reasignar dentro de su propio subárbol.",
    nodeNotFound: "No se encontró el nodo {{node}}.",
    parentNotFound: "No se encontró el nodo padre {{node}}.",
    invalidReorder: "La reordenación referencia un nodo no válido.",
    reorderSiblings: "Solo se pueden reordenar hermanos bajo el mismo padre.",
    invalidMerge: "La combinación referencia un nodo no válido.",
    invalidNodeParent: "La referencia del nodo o padre no es válida.",
    targetParentLevel: "El padre objetivo debe ser {{expected}}, pero es {{actual}}.",
    renameCollision: "Se detectó una colisión de nombres con el nodo {{node}}.",
  },
  cdtTable: {
    hierarchyNode: "Nodo de jerarquía",
    productCount: "Cantidad de productos",
    alerts: "Alertas",
    reorder: "Reordenar",
    products: "productos",
    clean: "Limpio",
    prioritySequence: "Secuencia de prioridad",
    priorityDescription: "Prioridad mínima derivada de los productos descendientes",
    zoomIn: "Acercar",
    zoomOut: "Alejar",
    fitView: "Ajustar vista",
    productCountValue: "{{count}} productos",
  },
  cdtHeader: {
    tree: "Árbol",
    list: "Lista",
    priority: "Prioridad CDT",
    undo: "Deshacer",
    redo: "Rehacer",
    save: "Guardar",
    togglePriority: "Alternar insignias de prioridad",
    expandAll: "Expandir todo",
    collapseAll: "Contraer todo",
    resolveAlerts: "Resolver alertas",
    category: "CDT 0: Categoría",
    categoryName: "Categoría",
    segmentName: "Segmento",
    brandName: "Marca",
    flavourName: "Sabor",
    revertChanges: "Revertir cambios",
    saveNew: "Guardar nuevo",
    next: "Siguiente",
  },
  cdtRename: {
    title: "Colisión al renombrar grupo",
    existing: '¡Ya existe un grupo hermano llamado "{{name}}"!',
    mergeDescription: "El renombrado combinará ambos grupos y sus subárboles descendientes.",
    cancel: "Cancelar",
    close: "Cerrar diálogo de colisión de nombres",
    confirm: "Confirmar combinación",
  },
  cdtDelete: {
    title: "Confirmación de eliminación del subárbol",
    question: '¿Seguro que deseas eliminar el grupo "{{name}}"?',
    descendants: "Nodos CDT descendientes que se eliminarán:",
    close: "Cerrar confirmación de eliminación",
    cancel: "Cancelar",
    delete: "Eliminar subárbol",
  },
  cdtReset: {
    title: "Restablecer con datos ETL",
    warning: "Esto descarta la personalización guardada y los cambios de jerarquía sin guardar.",
    close: "Cerrar confirmación de restablecimiento",
    cancel: "Cancelar",
    confirm: "Restablecer con ETL",
    resetting: "Restableciendo…",
  },
  cdtCanvas: {
    categoryRoot: "Categoría",
    selectNode: "Seleccionar {{name}}",
    expandNode: "Expandir {{name}}",
    collapseNode: "Contraer {{name}}",
    moveUp: "Mover arriba",
    moveDown: "Mover abajo",
  },
  cdtNodeInspector: {
    title: "Inspector de nodos",
    group: "Grupo",
    categoryRoot: "Raíz de categoría",
    nodeName: "Nombre del nodo",
    aggregateItemCount: "Elementos en la jerarquía, incluidos descendientes ({{count}})",
    immediateChildren: "Hijos inmediatos",
    noImmediateChildren: "No hay hijos inmediatos.",
    viewProducts: "Ver productos",
    saveBeforeViewingProducts: "Guarda la jerarquía antes de ver productos.",
    addSubgroup: "Agregar subgrupo {{level}}",
    newGroupName: "Nombre del grupo {{level}} nuevo",
    addSubgroupAction: "Agregar subgrupo",
    deleteGroup: "Eliminar este grupo",
    activeAlerts: "Alertas activas ({{count}})",
    noActiveAlerts: "No hay alertas activas para este nodo.",
    suggestionsComingSoon: "Sugerencias (próximamente)",
    comingSoon: "Próximamente",
    selectNode: "Selecciona un nodo en el lienzo para inspeccionar sus detalles.",
    submitRename: "Confirmar renombrado",
    saveParentFirst: "Guarda el nuevo padre antes de agregar otro subgrupo",
  },
  cdtProducts: {
    regionLabel: "Productos en {{name}}",
    title: "Productos en {{name}}",
    directCountOne: "1 producto directo",
    directCount: "{{count}} productos directos",
    searchPlaceholder: "Buscar productos",
    close: "Cerrar panel de productos",
    loading: "Cargando productos…",
    loadFailed: "No se pudieron cargar los productos.",
    retry: "Reintentar",
    noProducts: "No hay productos asignados directamente a este nodo.",
    noSearchResults: "Ningún producto coincide con esta búsqueda.",
    pageSize: "Filas por página",
    product: "Producto",
    segment: "Segmento",
    subcategory: "Subcategoría",
    brand: "Marca",
    sku: "SKU",
    price: "Precio",
    position: "Posición",
    notAvailable: "No disponible",
  },
  cdtAlerts: {
    renamePlaceholder: "Nuevo nombre...",
    rename: "Renombrar",
    groupScope: "Grupo: {{name}}",
    draftScope: "Alertas del borrador",
    affectedGroup: "Grupo afectado: {{name}}",
    affectedDraft: "Ámbito afectado: borrador actual",
    similarGroup: 'El nombre del grupo CDT "{{name}}" se repite bajo el mismo padre',
    guidedTitle: "Resolución guiada de alertas",
    close: "Cerrar resolución de alertas",
    similarGroups: "Grupos similares:",
    affectedScope: "Ámbito afectado",
    affectedAlertScope: "Ámbito de alertas afectadas",
    allActive: "Todas las alertas activas",
    showing: "Mostrando {{shown}} de {{total}} alertas activas",
    noAlerts: "No hay alertas activas de jerarquía CDT.",
    noScopedAlerts: "No hay alertas activas en este ámbito afectado.",
    focusGroup: "Enfocar grupo",
    editRequired: "Se requiere el modo de edición para reparar",
  },
  cdtStudioTab: {
    tabLabel: "CDT Studio",
    configurationLabel: "Configuración de CDT Studio",
    unavailable: {
      notPlanogram: "CDT Studio solo está disponible en el editor de planogramas.",
      missingMerchflow: "Abre este planograma desde un merchflow para usar CDT Studio.",
      unsupportedMerchant: "CDT Studio actualmente solo está disponible para Walmart.",
      missingPermission: "Tu rol necesita permiso para editar CDT y usar CDT Studio.",
      categoryUnavailable:
        "CDT Studio necesita exactamente una categoría en el contexto del planograma actual.",
      configurationRequired:
        "No hay una configuración de CDT Studio disponible para esta categoría. Abre Categorías y guarda la configuración de esta categoría.",
    },
  },
  planogramHighlights: {
    pinLabelToTop: "Fijar {{label}} arriba",
    unpinLabel: "Desfijar {{label}}",
  },
  cdtPriorityOrder: {
    tabLabel: "Reordenar CDTs",
    cdtLevel: "Nivel CDT",
    cdt0: "CDT0",
    cdt1: "CDT1",
    cdt2: "CDT2",
    cdt3: "CDT3",
    searchPlaceholder: "Buscar CDT o producto",
    expandGroup: "Expandir {{groupName}}",
    collapseGroup: "Contraer {{groupName}}",
    cdt0NoPriority: "CDT0 usa variante y no tiene campo de prioridad guardado",
    productCount: "{{count}} producto",
    productCountPlural: "{{count}} productos",
    unplaced: "Sin colocar",
    includesUnplaced: "Incluye sin colocar",
    ranged: "En surtido",
    unranged: "Sin surtido",
    placementLabel: "Bahía {{bayNo}}, Estante {{shelfNo}}",
    pegboardPlacementLabel: "Tablero {{pegboardId}}, agujero {{holeNoLeft}}, {{holeNoTop}}",
    mixed: "Mixto",
    noCdt: "(Sin CDT)",
    noData: "Sin datos de prioridad CDT",
    mode: "Modo",
    imageMode: "Imagen",
    labelMode: "Etiqueta",
    productCanvas: "Productos CDT",
    selectProduct: "Seleccionar {{productName}}",
    moveProductLeft: "Mover {{productName}} a la izquierda",
    moveProductRight: "Mover {{productName}} a la derecha",
    zoom: "Zoom",
    zoomIn: "Acercar",
    zoomOut: "Alejar",
    resetZoom: "Restablecer zoom",
    viewportControls: "Controles de vista del planograma",
    resetViewport: "Restablecer vista",
    reset: "Restablecer",
    planogramContainers: "Contenedores del planograma",
    planogramProducts: "Productos del planograma",
    planogramPegboards: "Tableros de clavijas del planograma",
    planogramPlacements: "Colocaciones del planograma",
    planogramShelves: "Estantes del planograma",
    planogramFixtures: "Accesorios del planograma",
    selectContainer: "Seleccionar {{label}}",
    selectPegboard: "Seleccionar tablero de clavijas {{id}}",
    selectPlacement: "Seleccionar {{label}}",
    selectShelf: "Seleccionar estante {{shelfNo}}",
    selectFixture: "Seleccionar {{fixtureName}} {{shelfNo}}",
    shelf: "Estante {{shelfNo}}",
    fixture: "{{fixtureName}} {{shelfNo}}",
    riserFixture: "elevador",
    hangcellFixture: "celda colgante",
    moveLeft: "Mover a la izquierda",
    moveRight: "Mover a la derecha",
    resizeShelfWider: "Ampliar el estante {{shelfNo}}",
    resizeShelfNarrower: "Estrechar el estante {{shelfNo}}",
    resizeShelfWiderText: "Ampliar estante",
    resizeShelfNarrowerText: "Estrechar estante",
    resizeFixtureNarrower: "Estrechar {{fixtureName}} {{shelfNo}}",
    resizeFixtureNarrowerText: "Estrechar accesorio",
    resizeFixtureWider: "Ampliar {{fixtureName}} {{shelfNo}}",
    resizeFixtureWiderText: "Ampliar accesorio",
    invalidPegboardHole: "Elige un orificio disponible en el tablero de clavijas.",
    shelfCollision: "Mueve el estante a una posición libre.",
    shelfResizeCollision: "Elige un tamaño que no se superponga con otro accesorio.",
    shelfMoveFailed: "No se pudo mover el estante. Prueba otra posición.",
    shelfResizeFailed: "No se pudo cambiar el tamaño del accesorio. Prueba otro tamaño.",
    pegboardMoveFailed:
      "No se pudo mover el producto en el tablero de clavijas. Prueba otro orificio.",
    productMoveRejected: "No se pudo mover el producto. Prueba otra posición.",
    planogramMoveFailed: "No se pudo completar el cambio del planograma. Inténtalo de nuevo.",
    labelOptions: {
      none: "Ninguno",
      brand: "Marca",
      categoryCode: "Código de categoría",
      cdt0: "CDT0",
      cdt1: "CDT1",
      cdt1Priority: "Prioridad CDT1",
      cdt2: "CDT2",
      cdt2Priority: "Prioridad CDT2",
      cdt3: "CDT3",
      cdt3Priority: "Prioridad CDT3",
      color: "Color",
      facings: "Frentes",
      name: "Nombre",
      price: "Precio",
      productCode: "Código de producto",
      quantity: "Cantidad",
      ranking: "Clasificación",
      salesAmount: "Importe de ventas",
      size: "Tamaño",
      subCategory: "Subcategoría",
      upc: "UPC",
      variant: "Variante",
    },
    confirmSnakingAriaLabel: "Confirmar patrón de serpenteo",
    snakingPatternAriaLabel: "Configuración del patrón de serpenteo",
    confirmSnakingTitle: "Confirmar patrón de serpenteo",
    confirmSnakingDescription:
      "Revisa las direcciones de las filas y el orden de filas de cada bahía antes de habilitar la edición de CDT.",
    snakingPatternTitle: "Configuración del patrón de serpenteo",
    snakingPatternDescription:
      "Revisa las direcciones configuradas de las filas y el orden de cada bahía.",
    bayLabel: "Bahía {{bayNo}}",
    shelfLabel: "Estante {{shelfName}}",
    bayShelfOrderAriaLabel: "Orden de estantes de la bahía {{bayNo}}",
    shelfDirectionAriaLabel: "Dirección del estante {{shelfName}}",
    pegboardRowLabel: "Tablero {{pegboardId}}, fila {{rowNo}}",
    pegboardRowDirectionAriaLabel: "Dirección de la fila {{rowNo}} del tablero {{pegboardId}}",
    selectShelfOrder: "Seleccionar orden de estantes",
    topFirst: "De arriba hacia abajo",
    bottomFirst: "De abajo hacia arriba",
    selectDirection: "Seleccionar dirección",
    leftToRight: "De izquierda a derecha",
    rightToLeft: "De derecha a izquierda",
    incompleteSnakingConfiguration:
      "Completa la dirección de cada bahía y fila de recorrido. Todos los segmentos de un estante combinado deben usar la misma dirección.",
    confirm: "Confirmar",
    snakingUnavailableTitle: "Editor de planograma CDT no disponible",
    snakingUnavailableMessage:
      "Se admiten estantes REGULAR y tableros de clavijas. RISER, HANGCELL y otros accesorios no compatibles bloquean todo el editor de planograma CDT.",
    confirmSnakingBeforeDrag:
      "Confirma la configuración del patrón de serpenteo antes de usar arrastrar y soltar de CDT.",
    priorityOrderViolation:
      "Violación del orden de prioridad: el orden de recorrido no coincide con la prioridad CDT3 almacenada ascendente. Arrastrar y soltar está deshabilitado.",
    duplicateCdtPath:
      "Ruta CDT duplicada: el movimiento CDT y el recálculo de prioridades están deshabilitados hasta corregir el duplicado.",
    duplicateCdtPathDetails: "{{path}} — productos: {{productCodes}}",
    recalculationFailed:
      "No se pudo completar el recálculo total de prioridades. El planograma no ha cambiado.",
    productMoveFailed:
      "No se pudo mover el producto porque no se pudieron recalcular las prioridades CDT. El planograma no ha cambiado.",
    recalculatePriorities: "Recálculo total de prioridades",
    viewSnakingPattern: "Ver patrón de serpenteo",
    hideSnakingPattern: "Ocultar patrón de serpenteo",
  },
  unsavedChanges: {
    title: "Cambios sin guardar",
    description:
      "Tienes cambios sin guardar en este planograma. Guárdalos antes de salir o sal de todos modos y perderás los cambios.",
    keepEditing: "Seguir editando",
    leaveAnyway: "Salir de todos modos",
    save: "Guardar",
    saveFailed: "No se pudieron guardar los cambios del planograma",
  },
  productsCdtsChanged: {
    title: "Los CDT de los productos han cambiado",
    description:
      "Los cambios de CDT de los productos solo se pueden guardar en el planograma base o también actualizar los CDT de los productos de todas las tiendas",
    saveOnly: "Guardar solo",
    saveAndUpdateProducts: "Guardar y actualizar productos",
  },
  planogram: {
    facingsAndPlacement: "Frentes y colocación",
    assortment: "Surtido",
  },
  basePog: {
    unavailableTitle: "No hay un planograma base",
    unavailableMessage:
      "Esta tienda se importó desde PSA y no tiene un planograma base para revisar.",
  },
  sidebar: {
    performance: "Rendimiento",
    assortment: "Surtido",
    spacePlanning: "Planificación Espacio",
    imagine: "Imagine",
    categories: "Categorías",
    stores: "Tiendas",
    products: "Productos",
    fixtures: "Mobiliario",
    projects: "Proyectos",
    merchflows: "Merchflows",
    storeSummary: "Resumen de Tienda",
    users: "Usuarios",
    audit: "Auditoría",
    profile: "Perfil",
    settings: "Configuración",
    logout: "Cerrar Sesión",
  },
  storesPickList: {
    columnStore: "TIENDA",
    columnBaySize: "TAMAÑO DE BAHÍA",
    columnBusinessType: "TIPO DE NEGOCIO",
    columnActivityType: "TIPO DE ACTIVIDAD",
    searchPlaceholder: "Buscar tiendas",
    selectedCount: "{{selected}} seleccionadas / {{total}} en filtro",
    loadingStores: "Cargando tiendas...",
    nextButton: "Siguiente ({{count}} {{label}})",
    storeLabel: "tienda",
    storesLabel: "tiendas",
  },
};
